<template>
  <section class="bg-[#111] py-14 lg:py-20" aria-labelledby="newsletter-heading">
    <div class="max-w-screen-xl mx-auto px-6">
      <div class="bg-[#1e1e1e] rounded-[20px] px-6 py-10 sm:px-10 sm:py-12 lg:px-14
                  grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">

        <!-- Tekst -->
        <div class="text-center lg:text-left">
          <span class="inline-block bg-[#FFD500] text-[#111] font-black text-[0.68rem] tracking-[0.1em] uppercase px-3 py-1 rounded-[6px] mb-4">
            €10 korting cadeau
          </span>
          <h2 id="newsletter-heading"
              class="text-[clamp(1.875rem,4vw,2.75rem)] font-black text-white leading-[1.1] mb-4">
            Schrijf je in en krijg €10 korting
          </h2>
          <p class="text-[1.0625rem] text-[#aaa] leading-[1.7] max-w-[440px] mx-auto lg:mx-0">
            Meld je aan voor onze nieuwsbrief en ontvang <strong class="text-white">€10 korting op je eerste bestelling</strong>.
            Plus als eerste ons laatste nieuws, handige gidsen en exclusieve aanbiedingen.
          </p>
        </div>

        <!-- Formulier -->
        <form class="flex flex-col gap-4" @submit.prevent="onSubmit" novalidate>
          <!-- Honeypot (verborgen; alleen bots vullen dit) -->
          <input v-model="hp" type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true" class="hidden" />
          <!-- E-mail -->
          <div class="relative">
            <span class="absolute left-4 top-1/2 -translate-y-1/2 text-[#FFD500] text-lg" aria-hidden="true">*</span>
            <input
              v-model="email"
              type="email"
              required
              autocomplete="email"
              placeholder="Vul je e-mailadres in"
              aria-label="E-mailadres"
              class="w-full h-[56px] pl-9 pr-4 rounded-[10px] bg-white text-[#111] text-[1rem]
                     placeholder:text-[#888] outline-none border-2 border-transparent
                     focus:border-[#FFD500] transition-colors"
            />
          </div>

          <!-- Telefoon met landcode (optioneel) -->
          <div class="flex h-[56px] rounded-[10px] bg-white overflow-hidden border-2 border-transparent
                      focus-within:border-[#FFD500] transition-colors relative">
            <span class="flex items-center gap-2 pl-4 pr-3 border-r border-[#e5e5e5] shrink-0 select-none">
              <span class="text-[1.1rem] leading-none" aria-hidden="true">🇳🇱</span>
              <span class="text-[#111] font-semibold text-[0.95rem]">NL</span>
              <span class="text-[#666] text-[0.95rem]">+31</span>
            </span>
            <input
              v-model="phone"
              type="tel"
              inputmode="tel"
              autocomplete="tel"
              placeholder="Mobiel (optioneel)"
              aria-label="Mobiel telefoonnummer (optioneel)"
              class="flex-1 min-w-0 px-4 bg-transparent text-[#111] text-[1rem]
                     placeholder:text-[#888] outline-none"
            />
          </div>

          <!-- Toestemming -->
          <label class="flex items-start gap-3 text-[0.9rem] text-[#aaa] leading-[1.5] cursor-pointer mt-1">
            <input
              v-model="agreed"
              type="checkbox"
              class="mt-[3px] h-[18px] w-[18px] shrink-0 accent-[#FFD500] cursor-pointer"
            />
            <span>
              Ik ga akkoord met de
              <router-link to="/algemene-voorwaarden" class="text-[#FFD500] underline underline-offset-2 hover:text-[#ffb800]">
                algemene voorwaarden
              </router-link>
              en het
              <router-link to="/privacy" class="text-[#FFD500] underline underline-offset-2 hover:text-[#ffb800]">
                privacybeleid
              </router-link>.
            </span>
          </label>

          <!-- Foutmelding / succes -->
          <p v-if="error" class="text-[0.875rem] text-[#ff6b6b] -mt-1" role="alert">{{ error }}</p>
          <p v-if="success" class="text-[0.875rem] text-[#4ade80] -mt-1" role="status">
            Bedankt! Je bent ingeschreven. Je €10-kortingscode ontvang je per e-mail.
          </p>

          <!-- Knop -->
          <button
            type="submit"
            :disabled="submitting"
            class="nl-submit mt-2 h-[56px] w-full rounded-[10px] bg-[#FFD500] hover:bg-[#ffb800] text-[#111]
                   font-bold text-[1.0625rem] disabled:opacity-60 disabled:cursor-not-allowed
                   focus-visible:outline-[3px] focus-visible:outline-[#FFD500] focus-visible:outline-offset-4"
          >
            {{ submitting ? 'Bezig…' : 'Nu inschrijven' }}
          </button>
        </form>

      </div>
    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue'

const emit = defineEmits(['subscribe'])

const email = ref('')
const phone = ref('')
const hp = ref('')            // honeypot
const agreed = ref(false)
const error = ref('')
const success = ref(false)

const submitting = ref(false)

async function onSubmit() {
  error.value = ''
  success.value = false

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())
  if (!emailOk) {
    error.value = 'Vul een geldig e-mailadres in.'
    return
  }
  if (!agreed.value) {
    error.value = 'Ga akkoord met de voorwaarden om je in te schrijven.'
    return
  }

  submitting.value = true
  try {
    const res = await fetch('/api/newsletter-subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.value.trim(), phone: phone.value.trim(), source: 'newsletter', website: hp.value }),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.error || 'Aanmelden mislukt, probeer het opnieuw.')
    }
    emit('subscribe', { email: email.value.trim(), phone: phone.value.trim() })
    success.value = true
    email.value = ''
    phone.value = ''
    agreed.value = false
  } catch (e) {
    error.value = e.message
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.nl-submit {
  transition: background 150ms cubic-bezier(0.23, 1, 0.32, 1),
              transform 150ms cubic-bezier(0.23, 1, 0.32, 1),
              box-shadow 150ms cubic-bezier(0.23, 1, 0.32, 1);
}
.nl-submit:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 213, 0, 0.45);
}
.nl-submit:not(:disabled):active { transform: scale(0.98); }
@media (prefers-reduced-motion: reduce) { .nl-submit { transition: none; } }
</style>
